import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import * as store from '../lib/clubStore';
import { slugify } from '../lib/slug';
import PublishStateNote from '../components/PublishStateNote';

export default function SeasonManager() {
  const { clubId } = useParams();
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add-season form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSeason, setNewSeason] = useState({ id: '', name: '', status: 'coming_soon', availableDate: '', expiresDate: '', access: 'free', entitlementId: '' });
  // Track whether the admin has hand-edited the ID — until then it auto-follows the name as a slug.
  const [idEdited, setIdEdited] = useState(false);

  const openAddForm = () => {
    setNewSeason({ id: '', name: '', status: 'coming_soon', availableDate: '', expiresDate: '', access: 'free', entitlementId: '' });
    setIdEdited(false);
    setShowAddForm(true);
  };

  const handleNameChange = (name) => {
    // Auto-suggest the ID as a slug of the name until the admin edits it directly.
    setNewSeason((prev) => ({ ...prev, name, id: idEdited ? prev.id : slugify(name) }));
  };

  const handleIdChange = (id) => {
    setIdEdited(true);
    setNewSeason((prev) => ({ ...prev, id }));
  };

  // Inline class inputs: keyed by seasonId
  const [classInputs, setClassInputs] = useState({});

  // Inline entitlement inputs: keyed by seasonId
  const [entInputs, setEntInputs] = useState({});

  // Inline access mode (interest/free/paid): keyed by seasonId
  const [accessMap, setAccessMap] = useState({});

  // Inline status selects: keyed by seasonId
  const [statusMap, setStatusMap] = useState({});

  // Inline date inputs: keyed by seasonId
  const [dateInputs, setDateInputs] = useState({});

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await store.loadAllClubs();
      const clubSeasons = data.seasonMap[clubId] || [];
      setSeasons(clubSeasons);
      const ents = {};
      const statuses = {};
      const dates = {};
      const accesses = {};
      for (const s of clubSeasons) {
        ents[s.id] = s.entitlement_id || '';
        statuses[s.id] = s.status || 'available';
        dates[s.id] = { available: s.available_date || '', expires: s.expires_date || '' };
        accesses[s.id] = store.deriveSeasonAccess(s);
      }
      setEntInputs(ents);
      setStatusMap(statuses);
      setDateInputs(dates);
      setAccessMap(accesses);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => { reload(); }, [reload]);

  // Merge a single season into local state (replace by id, else append) and
  // seed its inline controls, mirroring what reload() does per season. Lets a
  // mutation update the page in place without setLoading(true) or a full
  // loadAllClubs() refetch (which blanks the page to 'Loading…').
  const mergeSeason = useCallback((season) => {
    setSeasons((prev) => {
      const exists = prev.some((s) => s.id === season.id);
      return exists ? prev.map((s) => (s.id === season.id ? season : s)) : [...prev, season];
    });
    setEntInputs((prev) => ({ ...prev, [season.id]: season.entitlement_id || '' }));
    setStatusMap((prev) => ({ ...prev, [season.id]: season.status || 'available' }));
    setDateInputs((prev) => ({ ...prev, [season.id]: { available: season.available_date || '', expires: season.expires_date || '' } }));
    setAccessMap((prev) => ({ ...prev, [season.id]: store.deriveSeasonAccess(season) }));
  }, []);

  const handleCreateSeason = async (overwrite = false) => {
    const { id, name, status, availableDate, expiresDate, access, entitlementId } = newSeason;
    if (!id || !name) { setError('Season ID and Name are required.'); return; }
    // A paid season needs its entitlement up front — otherwise the app treats it
    // as paid but can't sync it (fails the entitlement gate, renders nothing).
    if (access === 'paid' && !(entitlementId || '').trim()) {
      setError('Paid seasons require an entitlement ID.');
      return;
    }
    try {
      const created = await store.createSeason(clubId, { id, name, status, availableDate, expiresDate, access, entitlementId }, { overwrite });
      setError(null); // clear any stale banner — reload() used to do this
      setShowAddForm(false);
      setNewSeason({ id: '', name: '', status: 'coming_soon', availableDate: '', expiresDate: '', access: 'free', entitlementId: '' });
      setIdEdited(false);
      // Optimistic local update — append (or replace, on overwrite) the season
      // in state and seed its inline controls, instead of blanking the page to
      // 'Loading…' and refetching every club.
      mergeSeason(created);
    } catch (err) {
      // Collision guard: an existing season with this id would be clobbered.
      // Offer an explicit overwrite confirm instead of failing or silently replacing.
      if (err.code === 'season-exists' && !overwrite) {
        const ok = window.confirm(
          `A season with ID "${id}" already exists in this club.\n\n` +
          'Saving will REPLACE its name, status, dates, classes and documents. ' +
          'This cannot be undone.\n\nOverwrite the existing season?',
        );
        if (ok) { await handleCreateSeason(true); }
        return;
      }
      setError('Error creating season: ' + err.message);
    }
  };

  const handleStatusChange = async (seasonId, status) => {
    setStatusMap((prev) => ({ ...prev, [seasonId]: status }));
    try {
      await store.updateSeasonStatus(clubId, seasonId, status);
    } catch (err) {
      setError('Error updating status: ' + err.message);
    }
  };

  const handleAccessChange = async (seasonId, access) => {
    const prev = accessMap[seasonId];
    setAccessMap((m) => ({ ...m, [seasonId]: access }));
    // Paid needs an entitlement. If none is set yet, just reveal the entitlement
    // field locally — the access change is committed atomically with the
    // entitlement by handleSaveEntitlement. Never persist a paid season with no
    // entitlement (the app would treat it as paid but fail to sync it).
    if (access === 'paid') {
      const ent = (entInputs[seasonId] || '').trim();
      if (!ent) return; // pending — entitlement still required
      try {
        await store.updateSeasonAccess(clubId, seasonId, 'paid', { entitlementId: ent });
      } catch (err) {
        setAccessMap((m) => ({ ...m, [seasonId]: prev }));
        setError('Error updating access: ' + err.message);
      }
      return;
    }
    try {
      await store.updateSeasonAccess(clubId, seasonId, access);
      // Free and interest clear the entitlement server-side; mirror that locally.
      setEntInputs((e) => ({ ...e, [seasonId]: '' }));
    } catch (err) {
      setAccessMap((m) => ({ ...m, [seasonId]: prev }));
      setError('Error updating access: ' + err.message);
    }
  };

  const handleSaveEntitlement = async (seasonId) => {
    const val = (entInputs[seasonId] || '').trim();
    if (!val) {
      setError('Paid seasons require an entitlement ID.');
      return;
    }
    try {
      // Write access:'paid' + the entitlement atomically. This also finalises a
      // pending free/interest -> paid switch made via the access selector.
      await store.updateSeasonAccess(clubId, seasonId, 'paid', { entitlementId: val });
      setAccessMap((m) => ({ ...m, [seasonId]: 'paid' }));
    } catch (err) {
      setError('Error updating entitlement: ' + err.message);
    }
  };

  const handleSaveDates = async (seasonId) => {
    const d = dateInputs[seasonId] || { available: '', expires: '' };
    try {
      await store.updateSeasonDates(clubId, seasonId, d.available || null, d.expires || null);
    } catch (err) {
      setError('Error updating dates: ' + err.message);
    }
  };

  const handleAddClass = async (seasonId) => {
    const input = classInputs[seasonId] || { id: '', name: '' };
    if (!input.id) { setError('Class ID is required.'); return; }
    if (!/^[a-z0-9_]+$/.test(input.id)) { setError('Class ID must be lowercase letters, numbers, and underscores.'); return; }
    try {
      // addClass returns the canonical classes array (dedup'd by id) after the
      // write. Render that instead of a full refetch — server truth, no local
      // guess about how the write merged.
      const classes = await store.addClass(clubId, seasonId, input.id, input.name || input.id);
      setError(null); // clear any stale banner — reload() used to do this
      setClassInputs((prev) => ({ ...prev, [seasonId]: { id: '', name: '' } }));
      setSeasons((prev) => prev.map((s) => (s.id === seasonId ? { ...s, classes } : s)));
    } catch (err) {
      setError('Error adding class: ' + err.message);
    }
  };

  const handleRemoveClass = async (seasonId, classId) => {
    try {
      await store.removeClass(clubId, seasonId, classId);
      setError(null); // clear any stale banner — reload() used to do this
      // Optimistic local update instead of a full refetch.
      setSeasons((prev) => prev.map((s) => (
        s.id === seasonId ? { ...s, classes: (s.classes || []).filter((c) => c.id !== classId) } : s
      )));
    } catch (err) {
      setError('Error removing class: ' + err.message);
    }
  };

  if (loading) return <div className="page"><p>Loading seasons...</p></div>;

  return (
    <div className="page">
      <header className="portal-header">
        <Link to={`/clubs/${clubId}`} className="back-link">&larr; Club</Link>
        <h1>Seasons</h1>
      </header>

      <PublishStateNote mode="instant">
        <p>
          Season status, access mode, dates, entitlement and class list save
          immediately — no publish step. (Course content for a season is separate
          and does use draft&rarr;publish.)
        </p>
      </PublishStateNote>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <p style={{ fontSize: '0.85rem', color: '#888' }}>Club: <code>{clubId}</code></p>
        <button className="btn btn-primary btn-sm" onClick={openAddForm}>+ Add Season</button>
      </div>

      <div className="notice" style={{ fontSize: '0.85rem' }}>
        <strong>A season&rsquo;s classes are the source of truth.</strong> The class list you set
        here &mdash; each just an ID and a name &mdash; is what the app offers boats for
        registration and what the race schedule references by ID. Add the season&rsquo;s classes
        here first, then open{' '}
        <Link to={`/clubs/${clubId}/fleets`}>Fleet &amp; Class Management</Link> to give those same
        IDs their racing details (flag codes, fleets, start locations and order). The{' '}
        <em>entitlement</em> below is set per <em>season</em>, not per class.
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <button className="btn btn-sm" style={{ marginLeft: '0.5rem' }} onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {showAddForm && (
        <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f5f5f5', borderRadius: '6px' }}>
          <div className="form-row" style={{ flexWrap: 'wrap' }}>
            <div className="form-field form-field-narrow">
              <label className="form-label">Name</label>
              <input placeholder="Summer 2026" value={newSeason.name} onChange={(e) => handleNameChange(e.target.value)} autoFocus />
            </div>
            <div className="form-field form-field-narrow">
              <label className="form-label">Season ID</label>
              <input placeholder="summer-2026" value={newSeason.id} onChange={(e) => handleIdChange(e.target.value)} />
              <span style={{ fontSize: '0.7rem', color: '#888', marginTop: '0.15rem' }}>Auto-filled from the name; edit to customise.</span>
            </div>
            <div className="form-field form-field-narrow">
              <label className="form-label">Status</label>
              <select value={newSeason.status} onChange={(e) => setNewSeason({ ...newSeason, status: e.target.value })}>
                <option value="coming_soon">Coming Soon</option>
                <option value="available">Available</option>
              </select>
            </div>
            <div className="form-field form-field-narrow">
              <label className="form-label">Access</label>
              <select value={newSeason.access} onChange={(e) => setNewSeason({ ...newSeason, access: e.target.value })}>
                <option value="free">Free — everyone</option>
                <option value="paid">Paid — entitlement</option>
                <option value="interest">Interest only</option>
              </select>
              <span style={{ fontSize: '0.7rem', color: '#888', marginTop: '0.15rem' }}>Free renders for all users with no purchase.</span>
            </div>
            {newSeason.access === 'paid' && (
              <div className="form-field form-field-narrow">
                <label className="form-label">Entitlement</label>
                <input
                  style={{ fontFamily: 'monospace' }}
                  placeholder="cc_club_season"
                  value={newSeason.entitlementId}
                  onChange={(e) => setNewSeason({ ...newSeason, entitlementId: e.target.value })}
                />
                <span style={{ fontSize: '0.7rem', color: newSeason.entitlementId.trim() ? '#888' : '#c0392b', marginTop: '0.15rem' }}>
                  Required for paid seasons.
                </span>
              </div>
            )}
            <div className="form-field form-field-narrow">
              <label className="form-label">Available</label>
              <input type="date" value={newSeason.availableDate} onChange={(e) => setNewSeason({ ...newSeason, availableDate: e.target.value })} />
            </div>
            <div className="form-field form-field-narrow">
              <label className="form-label">Expires</label>
              <input type="date" value={newSeason.expiresDate} onChange={(e) => setNewSeason({ ...newSeason, expiresDate: e.target.value })} />
            </div>
            <div className="form-field" style={{ flex: 'none', alignSelf: 'flex-end' }}>
              <div style={{ display: 'flex', gap: '0.35rem' }}>
                <button className="btn btn-primary btn-sm" onClick={() => handleCreateSeason()}>Create</button>
                <button className="btn btn-sm" onClick={() => setShowAddForm(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {seasons.length === 0 && (
        <div className="empty-state">No seasons yet. Add one above.</div>
      )}

      {seasons.map((season) => {
        const classes = season.classes || [];
        const clsInput = classInputs[season.id] || { id: '', name: '' };
        return (
          <div key={season.id} style={{ marginBottom: '0.75rem', padding: '0.6rem 0.75rem', border: '1px solid #e0e0e0', borderRadius: '6px', background: '#fff' }}>
            {/* Season header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{season.name || season.id}</span>
                <span style={{ fontSize: '0.75rem', color: '#888', marginLeft: '0.35rem' }}>{season.id}</span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <select
                  style={{ fontSize: '0.75rem', padding: '0.15rem 0.3rem' }}
                  value={statusMap[season.id] ?? 'available'}
                  onChange={(e) => handleStatusChange(season.id, e.target.value)}
                >
                  <option value="coming_soon">coming soon</option>
                  <option value="available">available</option>
                  <option value="expired">expired</option>
                </select>
                <span style={{ fontSize: '0.75rem', color: '#888' }}>
                  {classes.length} class{classes.length !== 1 ? 'es' : ''}
                </span>
              </div>
            </div>

            {/* Dates */}
            <div style={{ marginTop: '0.35rem', display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <span style={{ fontSize: '0.75rem', color: '#888' }}>Available:</span>
              <input
                type="date"
                style={{ fontSize: '0.75rem', padding: '0.15rem 0.4rem' }}
                value={(dateInputs[season.id] || {}).available || ''}
                onChange={(e) => setDateInputs((prev) => ({ ...prev, [season.id]: { ...(prev[season.id] || {}), available: e.target.value } }))}
              />
              <span style={{ fontSize: '0.75rem', color: '#888' }}>Expires:</span>
              <input
                type="date"
                style={{ fontSize: '0.75rem', padding: '0.15rem 0.4rem' }}
                value={(dateInputs[season.id] || {}).expires || ''}
                onChange={(e) => setDateInputs((prev) => ({ ...prev, [season.id]: { ...(prev[season.id] || {}), expires: e.target.value } }))}
              />
              <button className="btn btn-primary btn-sm" style={{ fontSize: '0.7rem', padding: '0.15rem 0.4rem' }} onClick={() => handleSaveDates(season.id)}>
                Save
              </button>
            </div>

            {/* Access mode */}
            <div style={{ marginTop: '0.35rem', display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <span style={{ fontSize: '0.75rem', color: '#888' }}>Access:</span>
              <select
                style={{ fontSize: '0.75rem', padding: '0.15rem 0.3rem' }}
                value={accessMap[season.id] ?? 'interest'}
                onChange={(e) => handleAccessChange(season.id, e.target.value)}
              >
                <option value="free">Free — everyone</option>
                <option value="paid">Paid — entitlement</option>
                <option value="interest">Interest only</option>
              </select>
              {(accessMap[season.id] ?? 'interest') === 'free' && (
                <span style={{ fontSize: '0.7rem', color: '#2e7d32' }}>Free — visible to everyone, no purchase needed.</span>
              )}
              {(accessMap[season.id] ?? 'interest') === 'interest' && (
                <span style={{ fontSize: '0.7rem', color: '#888' }}>Interest-only — no course bundle synced to the app.</span>
              )}
            </div>

            {/* Entitlement — only required for paid seasons */}
            {(accessMap[season.id] ?? 'interest') === 'paid' && (
              <div style={{ marginTop: '0.35rem', display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                <span style={{ fontSize: '0.75rem', color: '#888' }}>Entitlement:</span>
                <input
                  style={{ fontSize: '0.75rem', padding: '0.15rem 0.4rem', width: '160px', fontFamily: 'monospace' }}
                  placeholder="cc_club_season"
                  value={entInputs[season.id] ?? (season.entitlement_id || '')}
                  onChange={(e) => setEntInputs((prev) => ({ ...prev, [season.id]: e.target.value }))}
                />
                <button className="btn btn-primary btn-sm" style={{ fontSize: '0.7rem', padding: '0.15rem 0.4rem' }} onClick={() => handleSaveEntitlement(season.id)}>
                  Save
                </button>
                {!(entInputs[season.id] ?? (season.entitlement_id || '')).trim() && (
                  <span style={{ fontSize: '0.7rem', color: '#c0392b' }}>Required for paid seasons.</span>
                )}
              </div>
            )}

            {/* Classes */}
            {classes.length > 0 && (
              <div style={{ marginTop: '0.35rem', display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                {classes.map((c) => (
                  <span key={c.id} style={{ fontSize: '0.75rem', padding: '0.15rem 0.4rem', background: '#f0f0f5', borderRadius: '3px', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                    {c.name} <span style={{ opacity: 0.5 }}>({c.id})</span>
                    <span style={{ cursor: 'pointer', color: '#c0392b' }} onClick={() => handleRemoveClass(season.id, c.id)}>&times;</span>
                  </span>
                ))}
              </div>
            )}

            {/* Add class inline */}
            <div style={{ marginTop: '0.35rem', display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <input
                style={{ fontSize: '0.75rem', padding: '0.2rem 0.4rem', width: '80px' }}
                placeholder="cruiser"
                value={clsInput.id}
                onChange={(e) => setClassInputs((prev) => ({ ...prev, [season.id]: { ...clsInput, id: e.target.value } }))}
              />
              <input
                style={{ fontSize: '0.75rem', padding: '0.2rem 0.4rem', width: '100px' }}
                placeholder="Cruiser"
                value={clsInput.name}
                onChange={(e) => setClassInputs((prev) => ({ ...prev, [season.id]: { ...clsInput, name: e.target.value } }))}
              />
              <button className="btn btn-primary btn-sm" style={{ fontSize: '0.7rem', padding: '0.15rem 0.4rem' }} onClick={() => handleAddClass(season.id)}>
                + Class
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
